import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, PLP_LOGO, COMMUNITY_PHOTOS } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Shield,
  Wrench,
  Radio,
  Users,
  ArrowRight,
  MapPin,
  CheckCircle,
  AlertTriangle,
  Calendar,
  ChevronRight,
  Newspaper,
  Building2,
  Award,
  ChevronLeft
} from 'lucide-react';

const HomePage = () => {
  const [stats, setStats] = useState({ volunteers: 0, issues_fixed: 0, safety_reports: 0, total_issues: 0 });
  const [events, setEvents] = useState([]);
  const [homepagePhotos, setHomepagePhotos] = useState([]);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [siteContent, setSiteContent] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, eventsRes, photosRes, contentRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/events`),
          axios.get(`${API}/homepage-photos`),
          axios.get(`${API}/site-content`)
        ]);
        setStats(statsRes.data);
        setEvents(eventsRes.data.slice(0, 3));
        setHomepagePhotos(photosRes.data);
        if (contentRes.data && Object.keys(contentRes.data).length > 0) {
          setSiteContent(contentRes.data);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  // Get dynamic content with fallbacks
  const homepageContent = siteContent?.homepage || {};
  const heroTitle = homepageContent.hero_title || 'Together, We Build a Stronger Killarney';
  const heroSubtitle = homepageContent.hero_subtitle || 'Join Robyn Lyons and the PLP in creating positive change for our community';
  const heroButtonText = homepageContent.hero_button_text || 'Get Involved';
  const heroButtonLink = homepageContent.hero_button_link || '/get-involved';
  const heroSecondaryText = homepageContent.hero_secondary_button_text || 'Learn More';
  const heroSecondaryLink = homepageContent.hero_secondary_button_link || '/about';
  const featuresTitle = homepageContent.features_title || 'Your Community, Your Voice';
  const featuresSubtitle = homepageContent.features_subtitle || 'Killarney Connect puts the power in your hands. Here\'s how you can make a difference.';

  // Get photos by section
  const heroPhotos = homepagePhotos.filter(p => p.section === 'hero');
  const galleryPhotos = homepagePhotos.filter(p => p.section === 'gallery');
  const communityPhotos = homepagePhotos.filter(p => p.section === 'community');

  // Auto-rotate hero images
  useEffect(() => {
    if (heroPhotos.length > 1) {
      const interval = setInterval(() => {
        setCurrentHeroIndex(prev => (prev + 1) % heroPhotos.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [heroPhotos.length]);

  // Get current hero image (from admin photos or fallback)
  const currentHeroImage = heroPhotos.length > 0 
    ? (heroPhotos[currentHeroIndex]?.image_url?.startsWith('/api') 
        ? `${API.replace('/api', '')}${heroPhotos[currentHeroIndex].image_url}` 
        : heroPhotos[currentHeroIndex].image_url)
    : COMMUNITY_PHOTOS.main;
  
  const currentHeroTitle = heroPhotos.length > 0 && heroPhotos[currentHeroIndex]?.title 
    ? heroPhotos[currentHeroIndex].title 
    : "Building Community Together";
  
  const currentHeroDesc = heroPhotos.length > 0 && heroPhotos[currentHeroIndex]?.description 
    ? heroPhotos[currentHeroIndex].description 
    : "Killarney Constituency, Nassau, Bahamas";

  const features = [
    {
      title: 'Safer Together',
      description: 'Report suspicious activity and community concerns. Together we keep Killarney safe.',
      icon: Shield,
      color: 'bg-blue-500',
      link: '/safety-map',
      accent: 'border-l-blue-500'
    },
    {
      title: 'Fix It Forward',
      description: 'Report potholes, broken lights, and track repairs in real-time.',
      icon: Wrench,
      color: 'bg-amber-500',
      link: '/fix-it-forward',
      accent: 'border-l-amber-500'
    },
    {
      title: 'Community News',
      description: 'Stay informed with the latest news and updates from Killarney.',
      icon: Newspaper,
      color: 'bg-emerald-500',
      link: '/news',
      accent: 'border-l-emerald-500'
    },
    {
      title: 'Business Directory',
      description: 'Discover and support local businesses and schools in our community.',
      icon: Building2,
      color: 'bg-purple-500',
      link: '/directory',
      accent: 'border-l-purple-500'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-plp-gold/20 text-plp-blue px-4 py-2 rounded-full">
                <span className="w-2 h-2 bg-plp-gold rounded-full animate-pulse"></span>
                <span className="text-sm font-medium">Progressive Liberal Party</span>
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight">
                {heroTitle.includes(':') ? (
                  <>
                    {heroTitle.split(':')[0]}:{' '}
                    <span className="text-plp-blue">{heroTitle.split(':')[1]?.split('.')[0]?.trim()}</span>
                    {heroTitle.includes('.') && (
                      <>
                        {' '}
                        <span className="text-plp-gold">{heroTitle.split('.').slice(1).join('.').trim()}</span>
                      </>
                    )}
                  </>
                ) : (
                  <span className="text-plp-blue">{heroTitle}</span>
                )}
              </h1>
              
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
                {heroSubtitle}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to={heroButtonLink}>
                  <Button className="bg-plp-blue hover:bg-plp-blue-light text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all" data-testid="hero-safety-btn">
                    <Shield className="w-5 h-5 mr-2" />
                    {heroButtonText}
                  </Button>
                </Link>
                <Link to={heroSecondaryLink}>
                  <Button variant="outline" className="border-2 border-plp-blue text-plp-blue hover:bg-plp-blue/5 rounded-full px-8 py-6 text-lg" data-testid="hero-about-btn">
                    {heroSecondaryText}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Right Image */}
            <div className="relative animate-fade-in animate-delay-200">
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-plp-blue/10 to-plp-gold/10">
                <img
                  src={currentHeroImage}
                  alt="Killarney Community"
                  className="w-full h-auto min-h-[350px] max-h-[500px] object-contain transition-opacity duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-plp-blue/80 to-transparent p-6">
                  <p className="text-white font-heading font-bold text-xl">{currentHeroTitle}</p>
                  <p className="text-white/90 text-sm">{currentHeroDesc}</p>
                </div>
                {/* Hero Image Navigation */}
                {heroPhotos.length > 1 && (
                  <div className="absolute bottom-6 right-6 flex gap-2">
                    <button 
                      onClick={() => setCurrentHeroIndex(prev => prev === 0 ? heroPhotos.length - 1 : prev - 1)}
                      className="p-1.5 bg-white/30 hover:bg-white/50 rounded-full transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4 text-white" />
                    </button>
                    <button 
                      onClick={() => setCurrentHeroIndex(prev => (prev + 1) % heroPhotos.length)}
                      className="p-1.5 bg-white/30 hover:bg-white/50 rounded-full transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 text-white" />
                    </button>
                  </div>
                )}
                {/* Image Indicators */}
                {heroPhotos.length > 1 && (
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {heroPhotos.map((photo, idx) => (
                      <button
                        key={photo.id || `hero-indicator-${idx}`}
                        onClick={() => setCurrentHeroIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          idx === currentHeroIndex ? 'bg-white' : 'bg-white/40'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-plp-gold/30 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-plp-blue/20 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { id: 'volunteers', label: 'Volunteers', value: stats.volunteers, icon: Users },
              { id: 'issues-fixed', label: 'Issues Fixed', value: stats.issues_fixed, icon: CheckCircle },
              { id: 'safety-reports', label: 'Safety Reports', value: stats.safety_reports, icon: AlertTriangle },
              { id: 'active-issues', label: 'Active Issues', value: stats.total_issues, icon: Wrench }
            ].map((stat) => (
              <div key={stat.id} className="text-center p-6 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors">
                <stat.icon className="w-8 h-8 mx-auto mb-3 text-plp-blue" />
                <p className="text-3xl font-heading font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              {featuresTitle}
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              {featuresSubtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature) => (
              <Link key={feature.title} to={feature.link} data-testid={`feature-card-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <Card className={`h-full bg-white hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-l-4 ${feature.accent}`}>
                  <CardContent className="p-6 flex gap-4">
                    <div className={`w-14 h-14 rounded-2xl ${feature.color} flex items-center justify-center flex-shrink-0`}>
                      <feature.icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-xl text-slate-900">{feature.title}</h3>
                      <p className="mt-2 text-slate-600">{feature.description}</p>
                      <div className="mt-4 flex items-center text-plp-blue font-medium text-sm">
                        Learn more <ChevronRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Events Preview */}
      {events.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">Upcoming Events</h2>
              <Link to="/get-involved">
                <Button variant="ghost" className="text-plp-blue" data-testid="view-all-events">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {events.map((event) => (
                <Card key={event.id} className="bg-slate-50 hover:shadow-md transition-all" data-testid={`event-card-${event.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-plp-blue/10 rounded-xl flex flex-col items-center justify-center">
                        <Calendar className="w-5 h-5 text-plp-blue" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-plp-blue font-medium">{event.date} at {event.time}</p>
                        <h3 className="font-heading font-semibold text-lg text-slate-900 mt-1">{event.title}</h3>
                        <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">
                          <MapPin className="w-4 h-4" />
                          {event.location}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Community Gallery Row */}
      {galleryPhotos.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">Community Gallery</h2>
              <Link to="/gallery">
                <Button variant="ghost" className="text-plp-blue" data-testid="view-gallery">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {galleryPhotos.slice(0, 4).map((photo, idx) => (
                <div 
                  key={photo.id || idx} 
                  className="aspect-square rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-shadow"
                >
                  <img 
                    src={photo.image_url.startsWith('/api') 
                      ? `${API.replace('/api', '')}${photo.image_url}` 
                      : photo.image_url
                    }
                    alt={photo.title || `Community photo ${idx + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-plp-blue">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">
            Ready to Make a Difference?
          </h2>
          <p className="mt-4 text-lg text-white/80 max-w-2xl mx-auto">
            Join hundreds of Killarney residents working together for a safer, stronger community.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/get-involved">
              <Button className="bg-plp-gold hover:bg-plp-gold-light text-plp-blue font-bold rounded-full px-8 py-6 text-lg shadow-lg" data-testid="cta-volunteer-btn">
                <Users className="w-5 h-5 mr-2" />
                Volunteer Now
              </Button>
            </Link>
            <Link to="/safety-map">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg" data-testid="cta-report-btn">
                <Shield className="w-5 h-5 mr-2" />
                Report an Issue
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-plp-gold rounded-full flex items-center justify-center">
                  <span className="text-plp-blue font-heading font-bold">KC</span>
                </div>
                <span className="font-heading font-bold text-xl">Killarney Connect</span>
              </div>
              <p className="text-slate-400 max-w-md">
                Building a safer, stronger Killarney together. Authorized by the Progressive Liberal Party.
              </p>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-slate-400 hover:text-white transition-colors">About Robyn</Link></li>
                <li><Link to="/safety-map" className="text-slate-400 hover:text-white transition-colors">Safety Map</Link></li>
                <li><Link to="/fix-it-forward" className="text-slate-400 hover:text-white transition-colors">Fix It Forward</Link></li>
                <li><Link to="/get-involved" className="text-slate-400 hover:text-white transition-colors">Get Involved</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">Emergency</h4>
              <p className="text-slate-400 mb-2">For emergencies, dial:</p>
              <p className="text-2xl font-heading font-bold text-plp-gold">911</p>
              <p className="text-sm text-slate-500 mt-2">This platform is not a substitute for emergency services.</p>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
            <p>&copy; 2025 Killarney Connect. Authorized by the Progressive Liberal Party of The Bahamas.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
